import React, {FC, useCallback, useState} from 'react';
import styled from 'styled-components';

import {RendererOf} from '../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {CalendarsPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {DynamicScrollBoundaries} from '../../../../libs/shared/ui/src/components/scroll/dynamicScrollBoundaries';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {isEventEditable} from '../../../../srcCommon/helpers/models/calendarEventVersionHelpers';
import {CalendarEventStatusesEnum} from '../../../models/api/calendarEventModel';
import {
  CalendarEventVersionResponse,
  CalendarEventVersionVisualStatusesEnum,
} from '../../../models/api/calendarEventVersionModel';
import {VisualParticipantModel} from '../../../models/api/visualParticipantModel';
import {CalendarEventData} from '../../../store/panels/calendars/calendarsPanelSelectors';
import {CalendarEventEditorWithNotifyGuestsContainer} from '../editor/calendarEventEditorContainer';
import {buildFullCalendarEventId} from '../grid/fullCalendarHelpers';
import {CalendarEventViewerContentContainer} from './calendarEventViewerContentContainer';
import {CalendarEventViewerFooterContainer} from './calendarEventViewerFooterContainer';
import {SideCalendarEventViewerHeader} from './sideCalendarEventViewerHeader';

/*
 * Props.
 */

interface SideCalendarEventViewerProps {
  panelId: CalendarsPanelId;
  calendarEventData: CalendarEventData;
  visualParticipants: ReadonlyArray<VisualParticipantModel>;
  onBackClick: () => void;
  closeCalendarEventEditor: () => void;
  isCreatingFromOrphanEvent: boolean;
}

/*
 * Style.
 */

const StyledLayoutDiv = styled.div`
  display: grid;
  grid-template-rows: auto 1fr auto;
  min-height: 0;
`;

const StyledContentDiv = styled.div`
  padding: 0px 16px 16px 16px;
  display: grid;
  grid-auto-rows: min-content;
  grid-row-gap: 12px;
  overflow-y: scroll;
  word-break: break-word;
`;

const StyledFooterDiv = styled.div`
  position: sticky;
  bottom: 0px;

  display: grid;
  grid-row-gap: 12px;
  padding: 16px;

  background-color: ${(p) => p.theme.foreground};
`;

/*
 * Component.
 */

export const SideCalendarEventViewer: FC<SideCalendarEventViewerProps> = (props) => {
  const theme = useUserTheme();
  const {
    panelId,
    calendarEventData,
    visualParticipants,
    onBackClick,
    closeCalendarEventEditor,
    isCreatingFromOrphanEvent,
  } = props;
  const {calendarEventVersion, calendarEvent, calendar, calendarVersion, instanceStartDate} =
    calendarEventData;
  const {uid, response} = calendarEventVersion;
  const canEditResponseStatus = isEventEditable(calendar, calendarVersion, calendarEventVersion);
  const isDraft = calendarEventVersion.visualStatus === CalendarEventVersionVisualStatusesEnum.DRAFT;
  const isCancelled = calendarEvent.status === CalendarEventStatusesEnum.CANCELLED;
  const [isEditing, setIsEditing] = useState(false);

  /*
   * Event handlers.
   */

  const onEditorBackClick = useCallback(() => {
    if (isDraft) {
      onBackClick();
      return;
    }

    setIsEditing(false);
  }, [isDraft, onBackClick, setIsEditing]);

  /*
   * Render editor.
   */

  if (isEditing || isDraft) {
    return (
      <CalendarEventEditorWithNotifyGuestsContainer
        panelId={panelId}
        key={buildFullCalendarEventId(uid, calendarVersion.id, instanceStartDate)}
        calendarEventData={calendarEventData}
        onBackClick={onEditorBackClick}
        onSaveClick={onBackClick}
        onFocusedEventOverridesChange={undefined}
        closeCalendarEventEditor={closeCalendarEventEditor}
        isCreatingFromOrphanEvent={isCreatingFromOrphanEvent}
      />
    );
  }

  /*
   * Render viewer.
   */

  // Per product requirements, we explicitly choose to move focus away from the event after
  // responding to a recurring event (when the user is prompted to select a recurring save type).
  // This is to alleviate possible visual lag when rsvping to a "different" event (the instance from
  // the master or vice versa).
  const renderFooter: RendererOf<CalendarEventVersionResponse> = (r) => (
    <StyledFooterDiv>
      <CalendarEventViewerFooterContainer
        calendarEventData={calendarEventData}
        response={r}
        onRecurringSaveTypeSelection={onBackClick}
      />
    </StyledFooterDiv>
  );

  const shouldShowFooter = response && canEditResponseStatus && !isCancelled;

  return (
    <StyledLayoutDiv>
      <SideCalendarEventViewerHeader
        panelId={panelId}
        calendarEventData={calendarEventData}
        visualParticipants={visualParticipants}
        onEditClick={() => setIsEditing(true)}
        onBackClick={onBackClick}
      />
      <DynamicScrollBoundaries
        hasTopBoundary
        hasBottomBoundary={shouldShowFooter}
        color={theme.greys.shade30}
      >
        <StyledContentDiv>
          <CalendarEventViewerContentContainer
            panelId={panelId}
            calendarEventData={calendarEventData}
            visualParticipants={visualParticipants}
          />
        </StyledContentDiv>
      </DynamicScrollBoundaries>
      {shouldShowFooter && response && renderFooter(response)}
    </StyledLayoutDiv>
  );
};
